import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "../MesrequetesStyles/DemandeConges.css";
import {
    Calendar,
    Clock,
    Plus,
    FileText,
    AlertCircle,
    CheckCircle,
    XCircle,
    Upload,
    X,
    Eye,
    Trash2,
    FileText as FileTextIcon,
    TrendingUp,
    Award,
    RefreshCw,
} from "lucide-react";

// ✅ Leave requests (mine + create)
import {
    getMyLeaveRequestsRequest,
    createLeaveRequestRequest,
    clearLeaveRequestMessage,
    postponeLeaveRequestRequest,
} from "../../../../redux/actions/leaveRequestsActions";

// ✅ Param congés (typesAbsences + justificatifs)
import { fetchParamCongesRequest } from "../../../../redux/actions/paramCongesActions";

// ✅ Balance (soldes)
import {
    getUserSoldesRequest,
    checkBalanceRequest,
    cancelLeaveRequestRequest,
    getUserMovementsRequest,
} from "../../../../redux/actions/leaveBalanceActions";

const DemandeConges = () => {
    const dispatch = useDispatch();

    // ===================== REDUX: leave requests =====================
    const leaveState = useSelector((state) => state.leaveRequests || state.leaveRequest || {});
    const myRequests = leaveState.myRequests || [];
    const loading = !!leaveState.loading;
    const apiError = leaveState.error || "";
    const apiMessage = leaveState.message || "";

    // ===================== REDUX: param congés =====================
    const paramState = useSelector((state) => state.paramConges || {});
    const typesAbsences = paramState.typesAbsences || [];
    const justificatifsRules = paramState.justificatifs || [];
    const paramLoading = !!paramState.loading;

    // ===================== REDUX: balance (soldes) =====================
    const balanceState = useSelector((state) => state.leaveBalance || {});
    const userSoldes = balanceState.userSoldes || [];
    const movements = balanceState.movements || [];
    const balanceLoading = !!balanceState.loading;
    const balanceCheck = balanceState.balanceCheck || null;

    // ===================== REDUX: user info =====================
    const authState = useSelector((state) => state.auth || {});
    const currentUser = authState.user || authState.currentUser || {};
    const userId = currentUser.id || sessionStorage.getItem("uid");
    const companyId = currentUser.companyId || sessionStorage.getItem("companyId");

    // ===================== UI STATE =====================
    const [activeTab, setActiveTab] = useState("solde");
    const [showDemandeModal, setShowDemandeModal] = useState(false);
    const [showJustificatifModal, setShowJustificatifModal] = useState(false);
    const [showAnnulationModal, setShowAnnulationModal] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [soldeError, setSoldeError] = useState("");

    // ===================== COLLABORATEUR (TEMP) =====================
    const collaborateur = {
        matricule: currentUser.employeeCode || "EMP004",
        nom: currentUser.fullName || "Utilisateur",
        poste: currentUser.position || "",
        departement: currentUser.department || "",
    };

    // ===================== JOURS FÉRIÉS FIXES =====================
    const [fixedHolidays] = useState([
        { date: "01-01", description: "Nouvel an", selected: true },
        { date: "01-11", description: "Manifeste de l'Indépendance", selected: true },
        { date: "01-14", description: "Nouvel An Amazigh", selected: true },
        { date: "05-01", description: "Fête du Travail", selected: true },
        { date: "07-30", description: "Fête du Trône", selected: true },
        { date: "08-14", description: "Commémoration de l'Oued Ed-Dahab", selected: true },
        { date: "08-20", description: "Révolution du Roi et du Peuple", selected: true },
        { date: "08-21", description: "Fête de la Jeunesse", selected: true },
        { date: "11-06", description: "Anniversaire de la Marche Verte", selected: true },
        { date: "11-18", description: "Fête de l'Indépendance", selected: true },
    ]);

    // ===================== ✅ JOURS FÉRIÉS HIJRI (NOUVEAUX) =====================
    const [hijriHolidays] = useState([
        {
            name: "Aïd al-Fitr",
            date: "2026-03-20",
            duration: 2,
            selected: true
        },
        {
            name: "Aïd al-Adha",
            date: "2026-05-27",
            duration: 2,
            selected: true
        },
        {
            name: "Nouvel An Hijri",
            date: "2026-07-07",
            duration: 1,
            selected: true
        },
        {
            name: "Mawlid Nabawi",
            date: "2026-09-15",
            duration: 1,
            selected: true
        },
    ]);

    // ===================== JOURS DE TRAVAIL =====================
    const [workDays] = useState({
        lundi: { isWorking: true, hours: 8 },
        mardi: { isWorking: true, hours: 8 },
        mercredi: { isWorking: true, hours: 8 },
        jeudi: { isWorking: true, hours: 8 },
        vendredi: { isWorking: true, hours: 8 },
        samedi: { isWorking: false, hours: 0 },
        dimanche: { isWorking: false, hours: 0 },
    });

    const WEEKDAYS = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
    const WEEKDAYS_SHORT = ["L", "M", "M", "J", "V", "S", "D"];

    // ===================== ✅ SOLDES TRANSFORMÉS ET TRIÉS =====================
    const soldesConges = useMemo(() => {
        const transformed = (userSoldes || []).map((solde) => {
            const soldeInitial = Number(solde.soldeInitial || 0);
            const soldeAcquis = Number(solde.soldeAcquis || 0);
            const soldeConsomme = Number(solde.soldeConsomme || 0);
            const soldeRestant = Number(solde.soldeRestant || 0);

            return {
                id: solde.id,
                type: solde.absenceTypeNom || solde.absenceTypeName || "Type inconnu",
                code: solde.absenceTypeCode || "",
                couleur: solde.absenceTypeCouleur || "#2f478f",
                soldeTotal: soldeInitial + soldeAcquis,
                acquis: soldeAcquis,
                pris: soldeConsomme,
                enAttente: 0,
                restant: soldeRestant,
                periodeReference: `${solde.annee || new Date().getFullYear()}`,
                reportPossible: 0,
                dateExpiration: solde.dateExpiration || null,
            };
        });

        return transformed.sort((a, b) => b.restant - a.restant);
    }, [userSoldes]);

    // ===================== ✅ SOLDE PRINCIPAL = CONGÉ ANNUEL =====================
    const soldePrincipal = useMemo(() => {
        if (soldesConges.length === 0) return null;

        // Cherche un type dont le nom/code correspond à "congé annuel"
        const ANNUAL_KEYWORDS = ["annuel", "annual", "conge annuel", "congé annuel", "ca", "cp", "payé", "paye"];

        const found = soldesConges.find((s) => {
            const label = (s.type || "").toLowerCase().trim();
            const code = (s.code || "").toLowerCase().trim();
            return ANNUAL_KEYWORDS.some((kw) => label.includes(kw) || code.includes(kw));
        });

        // Si trouvé → retourne le congé annuel, sinon fallback sur le premier (le plus grand restant)
        return found || soldesConges[0];
    }, [soldesConges]);

    // ===================== FORMULAIRE =====================
    const [formData, setFormData] = useState({
        absenceTypeId: "",
        dateDebut: "",
        dateFin: "",
        commentaire: "",
        justificatif: null,
        autorisation: null,
    });

    const [justificatifUpload, setJustificatifUpload] = useState({
        demandeId: null,
        fichier: null,
    });

    // ===================== INIT LOAD =====================
    useEffect(() => {
        dispatch(getMyLeaveRequestsRequest());

        if (!typesAbsences || typesAbsences.length === 0) {
            dispatch(fetchParamCongesRequest());
        }

        if (userId && companyId) {
            dispatch(getUserSoldesRequest({ userId, companyId }));
        }
        if (userId && userId !== "0" && userId !== 0) {
            dispatch(getUserMovementsRequest(userId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (apiMessage || apiError) dispatch(clearLeaveRequestMessage());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    useEffect(() => {
        if (apiMessage && apiMessage.includes("succès")) {
            if (userId && companyId) {
                dispatch(getUserSoldesRequest({ userId, companyId }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiMessage]);

    // ===================== HELPERS =====================
    const safeStr = (v) => (v === null || v === undefined ? "" : String(v));

    const getAbsenceTypeLabel = (t) =>
        t?.label ||
        t?.libelle ||
        t?.name ||
        t?.nom ||
        t?.type ||
        "Type";

    const getAbsenceTypeColor = (t) =>
        t?.couleur ||
        t?.color ||
        t?.hexColor ||
        t?.backgroundColor ||
        "#2f478f";

    const calculerDuree = (debut, fin) => {
        const start = new Date(debut);
        const end = new Date(fin);
        return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        if (Number.isNaN(date.getTime())) return dateStr;
        return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    };

    const formatMonth = (date) => date.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

    // ===================== TYPES DYNAMIQUES (avec règles) =====================
    const typesCongesDisponibles = useMemo(() => {
        return (typesAbsences || []).map((t) => {
            const id = t?.id;
            const label = getAbsenceTypeLabel(t);
            const couleur = getAbsenceTypeColor(t);

            const requireJustificatif =
                !!t?.requireJustificatif ||
                !!t?.justificatifObligatoire ||
                !!t?.isJustificatifRequired;

            const requireAutorisation =
                !!t?.requireAutorisation ||
                !!t?.autorisationObligatoire ||
                !!t?.isAutorisationRequired;

            let inferredJustif = requireJustificatif;
            let inferredAuto = requireAutorisation;

            if (!inferredJustif && Array.isArray(justificatifsRules) && id) {
                inferredJustif = justificatifsRules.some((r) => {
                    const typeId = r?.absenceTypeId || r?.typeAbsenceId || r?.typeId;
                    const required = r?.required ?? r?.obligatoire ?? r?.isRequired;
                    return String(typeId) === String(id) && (required === true || required === 1);
                });
            }

            if (!inferredAuto && Array.isArray(justificatifsRules) && id) {
                inferredAuto = justificatifsRules.some((r) => {
                    const typeId = r?.absenceTypeId || r?.typeAbsenceId || r?.typeId;
                    const required = r?.requireAutorisation ?? r?.autorisationObligatoire ?? r?.isAutorisationRequired;
                    return String(typeId) === String(id) && (required === true || required === 1);
                });
            }

            return {
                id,
                value: label,
                label,
                couleur,
                requireJustificatif: inferredJustif || !!t?.justificationRequired,
                justificatifMandatory: !!t?.justificationMandatory,
                justificationType: t?.justificationType || "Justificatif",
                requireAutorisation: inferredAuto,
                maxDaysPerRequest: t?.maxDaysPerRequest,
                periodeDe: t?.periodeDe,
                periodeA: t?.periodeA,
                negativeBalanceAllowed: !!t?.negativeBalanceAllowed,
                maxNegativeBalance: t?.maxNegativeBalance,
                allowReschedule: t?.allowReschedule !== false,
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typesAbsences, justificatifsRules]);

    const selectedTypeObj = useMemo(() => {
        if (!formData.absenceTypeId) return null;
        return typesCongesDisponibles.find((t) => String(t.id) === String(formData.absenceTypeId)) || null;
    }, [formData.absenceTypeId, typesCongesDisponibles]);

    const selectedTypeLabel = selectedTypeObj?.label || "";

    // ===================== MAP API -> UI HISTORIQUE =====================
    const historiqueDemandes = useMemo(() => {
        return (myRequests || []).map((d) => {
            const deb = d.dateDebut || d.startDate || d.fromDate;
            const fin = d.dateFin || d.endDate || d.toDate;

            const duree =
                Number.isFinite(d.duree) ? d.duree :
                    (deb && fin ? calculerDuree(deb, fin) : 0);

            const createdAt =
                d.createdAt ||
                d.dateDemande ||
                d.requestDate ||
                d.datesDemande ||
                "";

            const typeNom =
                d.absenceTypeNom ||
                d.absenceTypeName ||
                d.absenceTypeLabel ||
                d.typeConge ||
                "Congé";

            const typeMatch = (typesCongesDisponibles || []).find((t) => t.label === typeNom);

            const statusRaw = safeStr(d.status || d.statut).toUpperCase();
            const statut =
                statusRaw === "PENDING" || statusRaw === "PENDING_MANAGER" || statusRaw === "PENDING_ADMIN" || statusRaw === "EN_ATTENTE" ? "enAttente" :
                    statusRaw === "APPROVED" || statusRaw === "APPROUVE" ? "approuve" :
                        statusRaw === "REJECTED" || statusRaw === "REFUSE" ? "refuse" :
                            statusRaw === "CANCELLED" || statusRaw === "ANNULE" ? "annule" :
                                statusRaw === "POSTPONED" || statusRaw === "REPORTE" ? "reporte" :
                                    (d.status || d.statut || "enAttente");

            const motifRejet = d.motifRejet || d.rejectionReason || d.managerComment || d.adminComment || "";

            return {
                id: d.id,
                typeConge: typeNom,
                couleurType: typeMatch?.couleur || "#2f478f",
                dateDebut: deb,
                dateFin: fin,
                duree,
                datesDemande: createdAt ? String(createdAt).slice(0, 10) : "",
                commentaire: d.commentaire || d.comment || "",
                statut,
                justificatifFourni: !!d.justificatifFourni,
                justificatifNom: d.justificatifNom || d.justificatifName || null,
                autorisationFournie: !!d.autorisationFournie,
                autorisationNom: d.autorisationNom || d.autorisationName || null,
                motifRejet,
                employeeName: d.employeeName || "",
                employeeCode: d.employeeCode || "",
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myRequests, typesCongesDisponibles]);

    // ===================== ✅ CALENDAR HELPERS AMÉLIORÉS =====================

    const isFixedHoliday = (date) => {
        if (!date) return false;
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        const dateStr = `${month}-${day}`;
        return fixedHolidays.some((h) => h.date === dateStr && h.selected);
    };

    const isHijriHoliday = (date) => {
        if (!date) return false;

        return hijriHolidays.some((h) => {
            if (!h.selected || !h.date) return false;

            const holidayDate = new Date(h.date);
            const checkDate = new Date(date);
            holidayDate.setHours(0, 0, 0, 0);
            checkDate.setHours(0, 0, 0, 0);

            for (let i = 0; i < (h.duration || 1); i++) {
                const testDate = new Date(holidayDate);
                testDate.setDate(testDate.getDate() + i);
                testDate.setHours(0, 0, 0, 0);

                if (testDate.getTime() === checkDate.getTime()) return true;
            }
            return false;
        });
    };

    const isNonWorkingDay = (date) => {
        if (!date) return false;
        let dayIndex = date.getDay();
        dayIndex = dayIndex === 0 ? 6 : dayIndex - 1;
        const dayName = WEEKDAYS[dayIndex];
        return !workDays[dayName].isWorking;
    };

    const getCongeInfo = (date) => {
        if (!date) return null;
        const y = date.getFullYear();
        const m = (date.getMonth() + 1).toString().padStart(2, "0");
        const d = date.getDate().toString().padStart(2, "0");
        const dateStr = `${y}-${m}-${d}`;

        return historiqueDemandes.find((demande) => {
            if (demande.statut === "refuse" || demande.statut === "annule") return false;
            const debutDate = new Date(demande.dateDebut);
            const finDate = new Date(demande.dateFin);
            const checkDate = new Date(dateStr);
            debutDate.setHours(0, 0, 0, 0);
            finDate.setHours(0, 0, 0, 0);
            checkDate.setHours(0, 0, 0, 0);
            return checkDate >= debutDate && checkDate <= finDate;
        });
    };

    const getHolidayName = (date) => {
        if (!date) return "";
        const names = [];

        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        const dateStr = `${month}-${day}`;
        const fixedHoliday = fixedHolidays.find((h) => h.date === dateStr && h.selected);
        if (fixedHoliday) names.push(`🎉 ${fixedHoliday.description}`);

        const year = date.getFullYear();
        const m2 = (date.getMonth() + 1).toString().padStart(2, "0");
        const d2 = date.getDate().toString().padStart(2, "0");
        const fullDate = `${year}-${m2}-${d2}`;

        hijriHolidays.forEach((h) => {
            if (!h.selected || !h.date) return;

            const holidayDate = new Date(h.date);
            holidayDate.setHours(0, 0, 0, 0);

            for (let i = 0; i < (h.duration || 1); i++) {
                const test = new Date(holidayDate);
                test.setDate(test.getDate() + i);
                test.setHours(0, 0, 0, 0);

                const testStr = `${test.getFullYear()}-${(test.getMonth() + 1).toString().padStart(2, "0")}-${test.getDate().toString().padStart(2, "0")}`;

                if (testStr === fullDate) {
                    const dayLabel = h.duration > 1 ? ` (Jour ${i + 1}/${h.duration})` : "";
                    names.push(`🌙 ${h.name}${dayLabel}`);
                    break;
                }
            }
        });

        const congeInfo = getCongeInfo(date);
        if (congeInfo) {
            const statutLabel = congeInfo.statut === "enAttente" ? " (En attente)" : " (Approuvé)";
            names.push(`📅 ${congeInfo.typeConge}${statutLabel}`);
        }

        return names.join(" • ");
    };

    const getMonthData = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        let startOffset = firstDay.getDay() - 1;
        if (startOffset === -1) startOffset = 6;

        const daysInMonth = lastDay.getDate();
        const weeks = [];
        let currentWeek = [];

        for (let i = 0; i < startOffset; i++) currentWeek.push(null);

        for (let day = 1; day <= daysInMonth; day++) {
            if (currentWeek.length === 7) {
                weeks.push(currentWeek);
                currentWeek = [];
            }
            currentWeek.push(new Date(year, month, day));
        }

        while (currentWeek.length < 7) currentWeek.push(null);
        weeks.push(currentWeek);
        return weeks;
    };

    const getDayClass = (date) => {
        if (!date) return "dcDay--empty";

        const congeInfo = getCongeInfo(date);
        if (congeInfo) {
            if (congeInfo.statut === "enAttente") return "dcDay--pending";
            if (congeInfo.statut === "approuve") return "dcDay--approved";
        }

        if (isHijriHoliday(date)) return "dcDay--hijri";
        if (isFixedHoliday(date)) return "dcDay--holiday";
        if (isNonWorkingDay(date)) return "dcDay--weekend";

        return "dcDay--normal";
    };

    // ===================== BADGE =====================
    const getStatutBadge = (statut) => {
        const config = {
            enAttente: { label: "En attente", bg: "#f9fefd", color: "#455584", icon: Clock },
            approuve: { label: "Approuvé", bg: "#e9f8ef", color: "#0f6b3f", icon: CheckCircle },
            refuse: { label: "Refusé", bg: "#ffe7ea", color: "#8b1d2b", icon: XCircle },
            annule: { label: "Annulé", bg: "#f3f4f6", color: "#6b7280", icon: XCircle },
            reporte: { label: "Reporté", bg: "#e0f2fe", color: "#0369a1", icon: RefreshCw },
        };
        const { label, bg, color, icon: Icon } = config[statut] || config.enAttente;
        return (
            <span className="dcBadge" style={{ backgroundColor: bg, color }}>
                <Icon size={14} />
                {label}
            </span>
        );
    };

    // ===================== COUNTS =====================
    const pendingCount = historiqueDemandes.filter((d) => d.statut === "enAttente").length;
    const approvedCount = historiqueDemandes.filter((d) => d.statut === "approuve").length;
    const refusedCount = historiqueDemandes.filter((d) => d.statut === "refuse").length;
    const reportedCount = historiqueDemandes.filter((d) => d.statut === "reporte").length;

    // ===================== ✅ ONGLETS : Mes demandes avant Nouvelle demande =====================
    const tabs = [
        { id: "solde", label: "Solde", icon: TrendingUp },
        { id: "historique", label: "Mes demandes", icon: Clock },
        { id: "demande", label: "Nouvelle demande", icon: Plus },
    ];

    const combinedHistory = useMemo(() => {
        const requestsAsMovements = historiqueDemandes
            .filter((req) => ["approuve", "annule", "reporte"].includes(req.statut))
            .map((req) => ({
                id: `req-${req.id}`,
                employeeName: req.employeeName,
                employeeCode: req.employeeCode,
                dateMouvement: req.datesDemande || req.dateDebut,
                reference: `REF-${req.id}`,
                motif: `Du ${formatDate(req.dateDebut)} au ${formatDate(req.dateFin)}${req.commentaire ? ` - ${req.commentaire}` : ""}`,
                absenceTypeNom: req.typeConge,
                absenceTypeCouleur: req.couleurType,
                soldeInitialSnapshot: req.soldeInitial,
                soldeRestantSnapshot: req.soldeRestant,
                valeur: -Number(req.duree || 0),
                isLeaveRequest: true,
                statut: req.statut,
            }));

        const positiveMovements = (movements || [])
            .filter((m) => Number(m.valeur || 0) > 0)
            .map((m) => ({
                id: `mov-${m.id}`,
                employeeName: m.employeeName,
                employeeCode: m.employeeCode,
                dateMouvement: m.dateMouvement,
                reference: m.crediteurName ? `${m.crediteurName} (${m.crediteurMatricule || "-"})` : (m.reference || "SYS_AUTO"),
                motif: m.commentaire || m.motif || "Ajustement",
                absenceTypeNom: m.absenceTypeNom,
                absenceTypeCouleur: m.absenceTypeCouleur,
                soldeInitialSnapshot: m.soldeInitialSnapshot,
                soldeRestantSnapshot: m.soldeRestantSnapshot,
                valeur: Number(m.valeur || 0),
                isLeaveRequest: false,
                statut: "valide",
            }));

        return [...requestsAsMovements, ...positiveMovements].sort((a, b) => {
            const dateA = a.dateMouvement ? new Date(a.dateMouvement).getTime() : 0;
            const dateB = b.dateMouvement ? new Date(b.dateMouvement).getTime() : 0;
            return dateB - dateA;
        });
    }, [historiqueDemandes, movements]);

    // ===================== SUBMIT (API) =====================
    const handleSubmitDemande = (e) => {
        e.preventDefault();
        setSoldeError("");

        if (!formData.absenceTypeId) {
            setSoldeError("Veuillez sélectionner un type de congé.");
            return;
        }
        if (!formData.dateDebut || !formData.dateFin) {
            setSoldeError("Veuillez renseigner la date de début et la date de fin.");
            return;
        }

        const duree = calculerDuree(formData.dateDebut, formData.dateFin);

        if (selectedTypeObj?.maxDaysPerRequest && duree > selectedTypeObj.maxDaysPerRequest) {
            setSoldeError(`La durée choisie (${duree} jour${duree > 1 ? "s" : ""}) dépasse le maximum autorisé (${selectedTypeObj.maxDaysPerRequest} jour${selectedTypeObj.maxDaysPerRequest > 1 ? "s" : ""}).`);
            return;
        }
        if (selectedTypeObj?.periodeDe && new Date(formData.dateDebut) < new Date(selectedTypeObj.periodeDe)) {
            setSoldeError(`Ce type de congé ne peut pas être pris avant le ${formatDate(selectedTypeObj.periodeDe)}.`);
            return;
        }
        if (selectedTypeObj?.periodeA && new Date(formData.dateFin) > new Date(selectedTypeObj.periodeA)) {
            setSoldeError(`Ce type de congé ne peut pas être pris après le ${formatDate(selectedTypeObj.periodeA)}.`);
            return;
        }

        const typeCongeObj = soldesConges.find((s) => s.type === selectedTypeLabel);
        if (typeCongeObj && typeCongeObj.restant < duree) {
            if (selectedTypeObj?.negativeBalanceAllowed) {
                const futureBalance = typeCongeObj.restant - duree;
                if (selectedTypeObj.maxNegativeBalance && Math.abs(futureBalance) > selectedTypeObj.maxNegativeBalance) {
                    setSoldeError(`Le solde négatif maximum autorisé (${selectedTypeObj.maxNegativeBalance} jours) sera dépassé.`);
                    return;
                }
            } else {
                setSoldeError(
                    `La période choisie (${duree} jour${duree > 1 ? "s" : ""}) dépasse votre solde disponible de ${typeCongeObj.type} (${typeCongeObj.restant} jour${typeCongeObj.restant > 1 ? "s" : ""}).`
                );
                return;
            }
        }

        if (selectedTypeObj?.requireJustificatif && selectedTypeObj?.justificatifMandatory && !formData.justificatif) {
            setSoldeError(`Le document "${selectedTypeObj.justificationType}" est obligatoire pour ce type de congé.`);
            return;
        }
        if (selectedTypeObj?.requireAutorisation && !formData.autorisation) {
            setSoldeError("L'autorisation est obligatoire pour ce type de congé.");
            return;
        }

        const payload = {
            absenceTypeId: Number(formData.absenceTypeId),
            dateDebut: formData.dateDebut,
            dateFin: formData.dateFin,
            commentaire: formData.commentaire || "",
            requestType: "conge",
        };

        dispatch(createLeaveRequestRequest(payload));

        setFormData({
            absenceTypeId: "",
            dateDebut: "",
            dateFin: "",
            commentaire: "",
            justificatif: null,
            autorisation: null,
        });

    };

    const handleAnnulerDemande = () => {
        if (!selectedDemande?.id) return;
        dispatch(cancelLeaveRequestRequest(selectedDemande.id));
        setShowAnnulationModal(false);
        setSelectedDemande(null);
    };

    // ===================== JUSTIFICATIF (UI ONLY) =====================
    const handleUploadJustificatif = (e) => {
        e.preventDefault();
        setShowJustificatifModal(false);
        setJustificatifUpload({ demandeId: null, fichier: null });
    };

    return (
        <div className="dcPage">
            <div className="dcWrap">
                {/* Header */}
                <div className="dcHeader dcMax">
                    <h1>Mes congés et absences</h1>
                </div>

                <div className="dcCard dcMax">
                    {/* Tabs */}
                    <div className="dcTabs">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`dcTab ${activeTab === tab.id ? "is-active" : ""}`}
                                    type="button"
                                >
                                    <Icon size={18} />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>

                    <div className="dcBody">
                        {/* API error */}
                        {apiError && (
                            <div className="dcAlert" style={{ marginBottom: 14 }}>
                                <AlertCircle size={18} color="#9f1239" style={{ marginTop: 1 }} />
                                <div>
                                    <h4>Erreur</h4>
                                    <p>{apiError}</p>
                                </div>
                            </div>
                        )}

                        {activeTab === "solde" && (
                            <div>
                                <div>
                                    <h2 className="dcH2">Mon solde de congés</h2>
                                    <p className="dcSub">Vue d'ensemble de vos congés disponibles</p>
                                </div>

                                <div className="dcKpiGrid">
                                    <div className="dcBigKpi">
                                        <div className="dcBigKpiTop">
                                            <span className="dcBigLabel">
                                                {soldePrincipal ? soldePrincipal.type : "Congé annuel"}
                                            </span>
                                            <span className="dcBigMeta">{new Date().getFullYear()}</span>
                                        </div>
                                        <div className="dcBigValue">
                                            <span className="dcBigNumber">
                                                {soldePrincipal ? soldePrincipal.restant : 0}
                                            </span>
                                            <span className="dcBigUnit">jours</span>
                                        </div>
                                    </div>

                                    <div className="dcKpi dcKpi--yellow">
                                        <div className="dcKpiTop">
                                            <Clock size={16} color="#707ea7" />
                                            <span className="dcKpiNum">{pendingCount}</span>
                                        </div>
                                        <div className="dcKpiTitle">En attente</div>
                                        <div className="dcKpiSub">
                                            {historiqueDemandes.filter((d) => d.statut === "enAttente").reduce((acc, d) => acc + (d.duree || 0), 0)} jours
                                        </div>
                                    </div>

                                    <div className="dcKpi dcKpi--green">
                                        <div className="dcKpiTop">
                                            <CheckCircle size={16} color="#059669" />
                                            <span className="dcKpiNum">{approvedCount}</span>
                                        </div>
                                        <div className="dcKpiTitle">Approuvées</div>
                                        <div className="dcKpiSub">
                                            {historiqueDemandes.filter((d) => d.statut === "approuve").reduce((acc, d) => acc + (d.duree || 0), 0)} jours
                                        </div>
                                    </div>

                                    <div className="dcKpi dcKpi--red">
                                        <div className="dcKpiTop">
                                            <XCircle size={16} color="#dc2626" />
                                            <span className="dcKpiNum">{refusedCount}</span>
                                        </div>
                                        <div className="dcKpiTitle">Refusées</div>
                                        <div className="dcKpiSub">
                                            {historiqueDemandes.filter((d) => d.statut === "refuse").reduce((acc, d) => acc + (d.duree || 0), 0)} jours
                                        </div>
                                    </div>

                                    <div className="dcKpi dcKpi--gray">
                                        <div className="dcKpiTop">
                                            <XCircle size={16} color="#4b5563" />
                                            <span className="dcKpiNum">{historiqueDemandes.filter((d) => d.statut === "annule").length}</span>
                                        </div>
                                        <div className="dcKpiTitle">Annulées</div>
                                        <div className="dcKpiSub">
                                            {historiqueDemandes.filter((d) => d.statut === "annule").reduce((acc, d) => acc + (d.duree || 0), 0)} jours
                                        </div>
                                    </div>

                                    <div className="dcKpi dcKpi--blue">
                                        <div className="dcKpiTop">
                                            <RefreshCw size={16} color="#2563eb" />
                                            <span className="dcKpiNum">{reportedCount}</span>
                                        </div>
                                        <div className="dcKpiTitle">Reportées</div>
                                        <div className="dcKpiSub">
                                            {historiqueDemandes.filter((d) => d.statut === "reporte").reduce((acc, d) => acc + (d.duree || 0), 0)} jours
                                        </div>
                                    </div>
                                </div>

                                {/* États de mes congés */}
                                <div className="dcBox" style={{ marginTop: 16 }}>
                                    <h3 className="dcBoxTitle">
                                        <CheckCircle size={18} color="#10b981" />
                                        États de mes congés
                                    </h3>

                                    {loading || balanceLoading ? (
                                        <div className="dcEmpty">
                                            <Clock size={32} />
                                            <div>Chargement des mouvements...</div>
                                        </div>
                                    ) : combinedHistory.length === 0 ? (
                                        <div className="dcEmpty">
                                            <CheckCircle size={32} color="#9ca3af" />
                                            <div style={{ marginTop: 8 }}>Aucun mouvement trouvé</div>
                                        </div>
                                    ) : (
                                        <div className="dcTableWrap" style={{ marginTop: 12 }}>
                                            <table className="dcTable">
                                                <thead>
                                                <tr>
                                                    <th>Référence</th>
                                                    <th>Date de l'opération</th>
                                                    <th>Motif</th>
                                                    <th>Type</th>
                                                    <th>Débit</th>
                                                    <th>Crédit</th>
                                                    <th>Solde</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {combinedHistory.map((item, index) => {
                                                    const isPositive = Number(item.valeur || 0) > 0;
                                                    const isNegative = Number(item.valeur || 0) < 0;
                                                    return (
                                                        <tr key={item.id || index} style={{
                                                            backgroundColor: isPositive ? "#f0fdf4" : isNegative ? "#fef2f2" : "#fff",
                                                            borderLeft: isPositive ? "3px solid #16a34a" : isNegative ? "3px solid #ef4444" : "3px solid #6b7280",
                                                        }}>
                                                            <td>
                                                                <div style={{ fontWeight: 700, color: "#2f4595" }}>{item.employeeName || "-"}</div>
                                                                <div className="dcTiny">{item.employeeCode || item.reference || "-"}</div>
                                                            </td>
                                                            <td>{formatDate(item.dateMouvement)}</td>
                                                            <td className="dcSmall" style={{ maxWidth: 260 }}>{item.motif || "-"}</td>
                                                            <td>
                                                                <div className="dcTypeCell">
                                                                    <span className="dcDot" style={{ backgroundColor: item.absenceTypeCouleur || (isPositive ? "#16a34a" : "#ef4444") }} />
                                                                    <span>{item.absenceTypeNom || "-"}</span>
                                                                </div>
                                                            </td>
                                                            <td style={{ textAlign: "center", color: "#ef4444", fontWeight: 700 }}>{isNegative ? `${item.valeur}j` : "-"}</td>
                                                            <td style={{ textAlign: "center", color: "#16a34a", fontWeight: 700 }}>{isPositive ? `+${item.valeur}j` : "-"}</td>
                                                            <td style={{ textAlign: "center", fontWeight: 800 }}>{item.soldeRestantSnapshot != null ? `${item.soldeRestantSnapshot}j` : "-"}</td>
                                                        </tr>
                                                    );
                                                })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ===== MES DEMANDES ===== */}
                        {activeTab === "historique" && (
                            <div>
                                <h2 className="dcH2" style={{ marginBottom: 12 }}>
                                    Mes demandes
                                </h2>

                                <div className="dcTableWrap">
                                    <table className="dcTable">
                                        <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Période</th>
                                            <th>Durée</th>
                                            <th>Date demande</th>
                                            <th>Statut</th>
                                            <th>Justificatif</th>
                                            <th>Autorisation</th>
                                            <th>Actions</th>
                                        </tr>
                                        </thead>

                                        <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan={8} style={{ padding: 16 }}>
                                                    <div className="dcEmpty" style={{ minHeight: 140 }}>
                                                        <Clock size={28} />
                                                        <div>Chargement...</div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : historiqueDemandes.length === 0 ? (
                                            <tr>
                                                <td colSpan={8} style={{ padding: 16 }}>
                                                    <div className="dcEmpty" style={{ minHeight: 140 }}>
                                                        <FileText size={28} />
                                                        <div>Aucune demande trouvée</div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            historiqueDemandes.map((demande) => {
                                                const typeConf = (typesCongesDisponibles || []).find((t) => t.label === demande.typeConge);

                                                return (
                                                    <tr key={demande.id}>
                                                        <td>
                                                            <div className="dcTypeCell">
                                                                <span className="dcDot" style={{ backgroundColor: demande.couleurType }} />
                                                                <span style={{ fontWeight: 600, color: "#2f4595" }}>{demande.typeConge}</span>
                                                            </div>
                                                        </td>

                                                        <td>
                                                            <div>
                                                                <div className="dcSmall">{formatDate(demande.dateDebut)}</div>
                                                                {demande.dateDebut !== demande.dateFin && <div className="dcTiny">au {formatDate(demande.dateFin)}</div>}
                                                            </div>
                                                        </td>

                                                        <td style={{ fontWeight: 700, color: "#2f4595" }}>{demande.duree}j</td>
                                                        <td className="dcSmall">{formatDate(demande.datesDemande)}</td>
                                                        <td>{getStatutBadge(demande.statut)}</td>

                                                        <td className="dcSmall">
                                                            {demande.justificatifFourni ? (
                                                                <span style={{ color: "#16a34a", fontWeight: 700 }}>
                                                                    <CheckCircle size={14} /> {demande.justificatifNom}
                                                                </span>
                                                            ) : typeConf?.requireJustificatif ? (
                                                                <span style={{ color: "#dc3545", fontWeight: 700 }}>Requis</span>
                                                            ) : (
                                                                <span style={{ color: "#9ca3af" }}>—</span>
                                                            )}
                                                        </td>

                                                        <td className="dcSmall">
                                                            {demande.autorisationFournie ? (
                                                                <span style={{ color: "#16a34a", fontWeight: 700 }}>
                                                                    <CheckCircle size={14} /> {demande.autorisationNom}
                                                                </span>
                                                            ) : typeConf?.requireAutorisation ? (
                                                                <span style={{ color: "#dc3545", fontWeight: 700 }}>Requis</span>
                                                            ) : (
                                                                <span style={{ color: "#9ca3af" }}>—</span>
                                                            )}
                                                        </td>

                                                        <td>
                                                            <div className="dcActions">
                                                                {demande.statut === "refuse" && demande.motifRejet && (
                                                                    <button
                                                                        className="dcIconBtn"
                                                                        title="Voir motif"
                                                                        type="button"
                                                                        onClick={() => setSelectedDemande(demande)}
                                                                    >
                                                                        <Eye size={16} />
                                                                    </button>
                                                                )}

                                                                {demande.statut === "enAttente" && (
                                                                    <button
                                                                        className="dcIconBtn is-danger"
                                                                        title="Annuler"
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setSelectedDemande(demande);
                                                                            setShowAnnulationModal(true);
                                                                        }}
                                                                    >
                                                                        <Trash2 size={16} />
                                                                    </button>
                                                                )}

                                                                {demande.statut === "approuve" && typeConf?.allowReschedule !== false && (
                                                                    <>
                                                                        <button
                                                                            className="dcIconBtn is-danger"
                                                                            title="Annuler"
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setSelectedDemande(demande);
                                                                                setShowAnnulationModal(true);
                                                                            }}
                                                                        >
                                                                            <XCircle size={16} />
                                                                        </button>
                                                                        <button
                                                                            className="dcIconBtn is-primary"
                                                                            title="Reporter"
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setSelectedDemande(demande);
                                                                                setShowReportModal(true);
                                                                            }}
                                                                        >
                                                                            <RefreshCw size={16} />
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* ===== NOUVELLE DEMANDE ===== */}
                        {activeTab === "demande" && (
                            <div>
                                <h2 className="dcH2">Formulaire de demande de congé</h2>

                                {(paramLoading || balanceLoading || typesCongesDisponibles.length === 0) && (
                                    <div className="dcInfo" style={{ marginTop: 10 }}>
                                        <Clock size={16} />
                                        Chargement des types de congés et soldes...
                                    </div>
                                )}

                                {soldeError && (
                                    <div className="dcAlert">
                                        <AlertCircle size={18} color="#9f1239" style={{ marginTop: 1 }} />
                                        <div>
                                            <h4>Erreur</h4>
                                            <p>{soldeError}</p>
                                        </div>
                                    </div>
                                )}

                                <form className="dcForm" onSubmit={handleSubmitDemande}>
                                    <div className="dcField">
                                        <label>
                                            Type de congé <span className="dcReq">*</span>
                                        </label>

                                        <select
                                            className="dcSelect"
                                            value={formData.absenceTypeId}
                                            onChange={(e) => {
                                                setFormData({
                                                    ...formData,
                                                    absenceTypeId: e.target.value,
                                                    justificatif: null,
                                                    autorisation: null,
                                                });
                                                setSoldeError("");
                                            }}
                                            required
                                            disabled={loading || paramLoading || balanceLoading || typesCongesDisponibles.length === 0}
                                        >
                                            <option value="">Sélectionner un type</option>

                                            {typesCongesDisponibles.map((type) => {
                                                const solde = soldesConges.find((s) => s.type === type.label);
                                                const info = solde ? ` (${solde.restant} jour${solde.restant > 1 ? "s" : ""})` : "";
                                                return (
                                                    <option key={type.id} value={type.id}>
                                                        {type.label}
                                                        {info}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>

                                    <div className="dcGrid2">
                                        <div className="dcField">
                                            <label>
                                                Date de début <span className="dcReq">*</span>
                                            </label>
                                            <input
                                                className="dcInput"
                                                type="date"
                                                value={formData.dateDebut}
                                                onChange={(e) => setFormData({ ...formData, dateDebut: e.target.value })}
                                                required
                                                disabled={loading}
                                            />
                                        </div>

                                        <div className="dcField">
                                            <label>
                                                Date de fin <span className="dcReq">*</span>
                                            </label>
                                            <input
                                                className="dcInput"
                                                type="date"
                                                value={formData.dateFin}
                                                onChange={(e) => setFormData({ ...formData, dateFin: e.target.value })}
                                                required
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>

                                    {formData.dateDebut && formData.dateFin && (
                                        <div className="dcInfo">
                                            <Clock size={16} />
                                            Durée : {calculerDuree(formData.dateDebut, formData.dateFin)} jour(s)
                                        </div>
                                    )}

                                    <div className="dcField">
                                        <label>Commentaire / Motif</label>
                                        <textarea
                                            className="dcTextarea"
                                            value={formData.commentaire}
                                            onChange={(e) => setFormData({ ...formData, commentaire: e.target.value })}
                                            placeholder="Précisez le motif..."
                                            disabled={loading}
                                        />
                                    </div>

                                    {selectedTypeObj?.requireJustificatif && (
                                        <div className="dcField">
                                            <label>
                                                Justificatif <span className="dcReq">*</span>
                                            </label>

                                            <div className="dcUploadBox">
                                                <Upload size={30} color="#9ca3af" />
                                                <div className="dcUploadHint">Glissez-déposez ou cliquez</div>

                                                <input
                                                    type="file"
                                                    className="dcHiddenFile"
                                                    id="dc-justif"
                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                    onChange={(e) => setFormData({ ...formData, justificatif: e.target.files?.[0] || null })}
                                                    disabled={loading}
                                                />
                                                <label
                                                    htmlFor="dc-justif"
                                                    className="dcBtn dcBtn--primary"
                                                    style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                                                >
                                                    <Upload size={16} />
                                                    Parcourir
                                                </label>

                                                {formData.justificatif && <div className="dcUploadOk">✓ {formData.justificatif.name}</div>}
                                            </div>

                                            <div className="dcHelp">PDF, JPG, PNG (max 5Mo)</div>
                                        </div>
                                    )}

                                    {selectedTypeObj?.requireAutorisation && (
                                        <div className="dcField">
                                            <label>
                                                Autorisation <span className="dcReq">*</span>
                                            </label>

                                            <div className="dcUploadBox dcUploadBox--accent">
                                                <FileTextIcon size={30} color="#dc3545" />
                                                <div className="dcUploadHint">Document d'autorisation requis (RH, manager...)</div>

                                                <input
                                                    type="file"
                                                    className="dcHiddenFile"
                                                    id="dc-auto"
                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                    onChange={(e) => setFormData({ ...formData, autorisation: e.target.files?.[0] || null })}
                                                    disabled={loading}
                                                />
                                                <label
                                                    htmlFor="dc-auto"
                                                    className="dcBtn dcBtn--primary"
                                                    style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                                                >
                                                    <Upload size={16} />
                                                    Parcourir
                                                </label>

                                                {formData.autorisation && <div className="dcUploadOk">✓ {formData.autorisation.name}</div>}
                                            </div>

                                            <div className="dcHelp">PDF, JPG, PNG (max 5Mo)</div>
                                        </div>
                                    )}

                                    <div className="dcFormFooter">
                                        <button
                                            type="button"
                                            className="dcBtn dcBtn--ghost"
                                            onClick={() => {
                                                setFormData({
                                                    absenceTypeId: "",
                                                    dateDebut: "",
                                                    dateFin: "",
                                                    commentaire: "",
                                                    justificatif: null,
                                                    autorisation: null,
                                                });
                                                setSoldeError("");
                                            }}
                                            disabled={loading}
                                        >
                                            Réinitialiser
                                        </button>

                                        <button
                                            type="submit"
                                            className="dcBtn dcBtn--primary"
                                            disabled={loading || paramLoading || balanceLoading || !formData.absenceTypeId}
                                        >
                                            {loading ? "Envoi..." : "Soumettre la demande"}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </div>
                </div>

                {/* ===== MODALS ===== */}
                {showAnnulationModal && selectedDemande && (
                    <div className="dcModal">
                        <div className="dcModalPanel">
                            <div className="dcModalHeader">
                                <div>
                                    <h3 className="dcModalTitle">Annuler la demande</h3>
                                    <div className="dcModalSub">Votre solde sera restauré</div>
                                </div>
                                <button
                                    className="dcXBtn"
                                    type="button"
                                    onClick={() => {
                                        setShowAnnulationModal(false);
                                        setSelectedDemande(null);
                                    }}
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <div className="dcModalBody">
                                <div className="dcBox" style={{ boxShadow: "none" }}>
                                    <div className="dcTypeCell" style={{ marginBottom: 6 }}>
                                        <span className="dcDot" style={{ backgroundColor: selectedDemande.couleurType }} />
                                        <span style={{ fontWeight: 800, color: "#2f4595" }}>{selectedDemande.typeConge}</span>
                                    </div>
                                    <div className="dcSmall">
                                        Du {formatDate(selectedDemande.dateDebut)} au {formatDate(selectedDemande.dateFin)}
                                    </div>
                                    <div className="dcSmall">Durée : {selectedDemande.duree} jour(s)</div>
                                </div>

                                <div className="dcWarnBox" style={{ marginTop: 12 }}>
                                    <strong>Note :</strong> Le solde sera automatiquement restauré après annulation.
                                </div>
                            </div>

                            <div className="dcModalFooter">
                                <button
                                    className="dcBtn dcBtn--ghost"
                                    type="button"
                                    onClick={() => {
                                        setShowAnnulationModal(false);
                                        setSelectedDemande(null);
                                    }}
                                >
                                    Conserver
                                </button>
                                <button className="dcBtn dcBtn--danger" type="button" onClick={handleAnnulerDemande}>
                                    Confirmer l'annulation
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showJustificatifModal && (
                    <div className="dcModal">
                        <div className="dcModalPanel dcModalPanel--sm">
                            <div className="dcModalHeader">
                                <div>
                                    <h3 className="dcModalTitle">Ajouter un justificatif</h3>
                                    <div className="dcModalSub">Téléversement du document</div>
                                </div>
                                <button
                                    className="dcXBtn"
                                    type="button"
                                    onClick={() => {
                                        setShowJustificatifModal(false);
                                        setJustificatifUpload({ demandeId: null, fichier: null });
                                    }}
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <form onSubmit={handleUploadJustificatif}>
                                <div className="dcModalBody">
                                    <div className="dcUploadBox">
                                        <Upload size={34} color="#9ca3af" />
                                        <div className="dcUploadHint">Glissez-déposez ou cliquez</div>

                                        <input
                                            type="file"
                                            id="dc-modal-justif"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={(e) => setJustificatifUpload({ ...justificatifUpload, fichier: e.target.files?.[0] || null })}
                                            required
                                        />

                                        <label htmlFor="dc-modal-justif" className="dcBtn dcBtn--primary" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                            <Upload size={16} />
                                            Parcourir
                                        </label>

                                        {justificatifUpload.fichier && <div className="dcUploadOk">✓ {justificatifUpload.fichier.name}</div>}
                                    </div>

                                    <div className="dcHelp">PDF, JPG, PNG (max 5Mo)</div>
                                </div>

                                <div className="dcModalFooter">
                                    <button
                                        type="button"
                                        className="dcBtn dcBtn--ghost"
                                        onClick={() => {
                                            setShowJustificatifModal(false);
                                            setJustificatifUpload({ demandeId: null, fichier: null });
                                        }}
                                    >
                                        Annuler
                                    </button>
                                    <button type="submit" className="dcBtn dcBtn--primary" disabled={!justificatifUpload.fichier}>
                                        Télécharger
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {selectedDemande &&
                    !showAnnulationModal &&
                    activeTab === "historique" &&
                    selectedDemande.statut === "refuse" &&
                    selectedDemande.motifRejet && (
                        <div className="dcModal">
                            <div className="dcModalPanel dcModalPanel--sm">
                                <div className="dcModalHeader">
                                    <div>
                                        <h3 className="dcModalTitle">Motif de rejet</h3>
                                        <div className="dcModalSub">Détails</div>
                                    </div>
                                    <button className="dcXBtn" type="button" onClick={() => setSelectedDemande(null)}>
                                        <X size={18} />
                                    </button>
                                </div>

                                <div className="dcModalBody">
                                    <div className="dcBox" style={{ boxShadow: "none" }}>
                                        <div className="dcTypeCell" style={{ marginBottom: 6 }}>
                                            <span className="dcDot" style={{ backgroundColor: selectedDemande.couleurType }} />
                                            <span style={{ fontWeight: 800, color: "#2f4595" }}>{selectedDemande.typeConge}</span>
                                        </div>
                                        <div className="dcSmall">
                                            Du {formatDate(selectedDemande.dateDebut)} au {formatDate(selectedDemande.dateFin)}
                                        </div>
                                        <div className="dcWarnBox" style={{ marginTop: 12 }}>
                                            {selectedDemande.motifRejet}
                                        </div>
                                    </div>
                                </div>

                                <div className="dcModalFooter">
                                    <button className="dcBtn dcBtn--primary" type="button" onClick={() => setSelectedDemande(null)}>
                                        Fermer
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                {showReportModal && selectedDemande && (
                    <ReportModal
                        demande={selectedDemande}
                        onClose={() => {
                            setShowReportModal(false);
                            setSelectedDemande(null);
                        }}
                        onConfirm={(dates) => {
                            dispatch(postponeLeaveRequestRequest({
                                id: selectedDemande.id,
                                startDate: dates.startDate,
                                endDate: dates.endDate,
                            }));
                            setShowReportModal(false);
                            setSelectedDemande(null);
                        }}
                    />
                )}
            </div>
        </div>
    );
};

const ReportModal = ({ demande, onClose, onConfirm }) => {
    const [newStartDate, setNewStartDate] = useState("");
    const [newEndDate, setNewEndDate] = useState("");
    const [error, setError] = useState("");

    const oldDuration = useMemo(() => {
        if (!demande.dateDebut || !demande.dateFin) return 0;
        const start = new Date(demande.dateDebut);
        const end = new Date(demande.dateFin);
        return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    }, [demande]);

    const currentDuration = useMemo(() => {
        if (!newStartDate || !newEndDate) return 0;
        const start = new Date(newStartDate);
        const end = new Date(newEndDate);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
        return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    }, [newStartDate, newEndDate]);

    const autoAdjustEndDate = () => {
        if (!newStartDate || oldDuration <= 0) return;
        const end = new Date(newStartDate);
        end.setDate(end.getDate() + oldDuration - 1);
        setNewEndDate(end.toISOString().slice(0, 10));
        setError("");
    };

    const handleConfirm = () => {
        setError("");
        if (!newStartDate || !newEndDate) {
            setError("Veuillez sélectionner les deux dates.");
            return;
        }
        const start = new Date(newStartDate);
        const end = new Date(newEndDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (start < today) {
            setError("La nouvelle date de début ne peut pas être dans le passé.");
            return;
        }
        if (end < start) {
            setError("La date de fin ne peut pas être antérieure à la date de début.");
            return;
        }
        if (currentDuration !== oldDuration) {
            setError(`La durée doit rester de ${oldDuration} jours.`);
            return;
        }
        onConfirm({ startDate: newStartDate, endDate: newEndDate });
    };

    return (
        <div className="dcModal">
            <div className="dcModalPanel dcModalPanel--sm">
                <div className="dcModalHeader">
                    <div>
                        <h3 className="dcModalTitle">Reporter la demande</h3>
                        <div className="dcModalSub">Même durée: {oldDuration} jours</div>
                    </div>
                    <button className="dcXBtn" type="button" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>
                <div className="dcModalBody">
                    <div className="dcWarnBox" style={{ marginBottom: 16 }}>
                        Ancienne période : <b>{demande.dateDebut}</b> au <b>{demande.dateFin}</b>
                    </div>
                    <div className="dcGrid2">
                        <div className="dcField">
                            <label>Nouvelle date de début</label>
                            <input className="dcInput" type="date" value={newStartDate} onChange={(e) => setNewStartDate(e.target.value)} />
                        </div>
                        <div className="dcField">
                            <label>Nouvelle date de fin</label>
                            <div style={{ display: "flex", gap: 8 }}>
                                <input className="dcInput" type="date" value={newEndDate} onChange={(e) => setNewEndDate(e.target.value)} />
                                <button className="dcIconBtn is-primary" type="button" title="Ajuster selon la durée" onClick={autoAdjustEndDate}>
                                    <TrendingUp size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                    {error && (
                        <div className="dcAlert" style={{ marginTop: 14 }}>
                            <AlertCircle size={16} color="#ef4444" />
                            <p>{error}</p>
                        </div>
                    )}
                    <div className="dcInfo" style={{ marginTop: 14 }}>
                        <Clock size={16} />
                        Durée sélectionnée : {currentDuration} jour(s)
                    </div>
                </div>
                <div className="dcModalFooter">
                    <button className="dcBtn dcBtn--ghost" type="button" onClick={onClose}>Annuler</button>
                    <button className="dcBtn dcBtn--primary" type="button" onClick={handleConfirm} disabled={currentDuration !== oldDuration}>
                        Confirmer le report
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DemandeConges;